import FormattedMessage from 'I18n/components/FormattedMessage';
import { Flex } from 'foundations-components/components/Flex';
import { Text } from 'foundations-components/components/Text';
import { View } from 'foundations-components/components/View';
import { BreakString } from 'foundations-components/transitional/components/BreakString';
import { CardSection, CardWrapper } from 'foundations-components/transitional/components/Card';
import { Image } from 'foundations-components/transitional/components/Image';
import { Ribbon } from 'foundations-components/transitional/components/Ribbon';
import { TruncateMultiline } from 'foundations-components/transitional/components/TruncateMultiline';
import { useCallback, useMemo } from 'react';
import Rating from 'marketplace-ui-reviews/components/Rating';
import { STAR_RATING_FILLED } from 'marketplace-ui-reviews/constants/RatingTypes';
import PartnerTypeIcon from 'marketplace-ui-solutions-core/components/PartnerTypeIcon';
import * as ProfileTierLabelKeys from 'marketplace-ui-solutions-core/constants/ProfileTierLabelKey';
import { getProfileTierLabelI18nKey } from 'marketplace-ui-solutions-core/utils/i18n';
import { getProfileLevelColorUse } from 'marketplace-ui-solutions-core/utils/profiles';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const iconMaxHeight = 48;
export function ProfileCard({
  description,
  logoUrl,
  name,
  partnerType,
  reviewSummary,
  tier
}) {
  const renderBody = useCallback(() => {
    if (!reviewSummary.get('overallRating') || !reviewSummary.get('reviewCount')) {
      return null;
    }
    return /*#__PURE__*/_jsx(CardSection, {
      paddingBlockStart: "300",
      paddingBlockEnd: "500",
      children: /*#__PURE__*/_jsx(Rating, {
        rating: reviewSummary.get('overallRating'),
        label: reviewSummary.get('reviewCount'),
        use: "card",
        showTextRating: true,
        type: STAR_RATING_FILLED
      })
    });
  }, [reviewSummary]);
  const renderedRibbon = useMemo(() => {
    const profileRibbonI18nKey = getProfileTierLabelI18nKey(tier, partnerType, ProfileTierLabelKeys.TAG);
    if (!profileRibbonI18nKey) {
      return null;
    }
    return /*#__PURE__*/_jsx(Ribbon, {
      use: getProfileLevelColorUse(partnerType, tier),
      position: "right",
      paddingInlineStart: "100",
      children: /*#__PURE__*/_jsxs(Flex, {
        alignItems: "center",
        children: [/*#__PURE__*/_jsx(PartnerTypeIcon, {
          partnerType: partnerType,
          size: 12,
          tier: tier,
          paddingInlineEnd: "100"
        }), /*#__PURE__*/_jsx(FormattedMessage, {
          message: profileRibbonI18nKey
        })]
      })
    });
  }, [partnerType, tier]);
  const subtitleText = useMemo(() => {
    // Use regex to strip HTML tags safely without DOM manipulation
    return description.replace(/<[^>]*>/g, '');
  }, [description]);
  const renderHeading = useCallback(() => /*#__PURE__*/_jsx(Text, {
    as: "h2",
    typography: "heading-300",
    marginBlockEnd: "0",
    children: name
  }), [name]);
  return /*#__PURE__*/_jsxs(CardWrapper, {
    compact: true,
    "data-test-id": "profile-card",
    children: [renderedRibbon, /*#__PURE__*/_jsx(CardSection, {
      paddingBlockStart: "1000",
      paddingBlockEnd: "100",
      children: /*#__PURE__*/_jsx(View, {
        blockSize: `${iconMaxHeight}px`,
        children: /*#__PURE__*/_jsx(Image, {
          src: logoUrl,
          maxBlockSize: '100%'
        })
      })
    }), /*#__PURE__*/_jsx(CardSection, {
      paddingBlockEnd: "200",
      scrollable: true,
      children: /*#__PURE__*/_jsxs(View, {
        maxBlockSize: "200px",
        children: [/*#__PURE__*/_jsx(BreakString, {
          children: renderHeading()
        }), /*#__PURE__*/_jsx(Text, {
          marginBlockStart: "200",
          children: /*#__PURE__*/_jsx(TruncateMultiline, {
            Paragraph: Text,
            maxChars: 100,
            text: subtitleText
          })
        })]
      })
    }), renderBody()]
  });
}